import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronRight, GraduationCap, Calculator, BookOpen, X, ZoomIn, ChevronLeft as ChevronLeftIcon } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import Navbar from './Navbar';
import Footer from './Footer';

// Lightbox Component
const Lightbox = ({ image, onClose }) => {
  if (!image) return null;
  
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/95"
        onClick={onClose}
      >
        <motion.button
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="absolute top-4 right-4 w-12 h-12 flex items-center justify-center bg-white/10 hover:bg-white/20 transition-colors"
          onClick={onClose}
        >
          <X size={24} className="text-white" />
        </motion.button>
        
        <motion.img
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.8, opacity: 0 }}
          src={image}
          alt="Enlarged view"
          className="max-w-[90vw] max-h-[90vh] object-contain"
          onClick={(e) => e.stopPropagation()}
        />
      </motion.div>
    </AnimatePresence>
  );
};

// Image Carousel Component
const ImageCarousel = ({ images, onImageClick }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  if (!images || images.length === 0) {
    return (
      <div className="card-technical p-12 text-center h-64 flex items-center justify-center">
        <p className="text-[#94A3B8] text-lg">Images coming soon...</p>
      </div>
    );
  }

  const nextImage = () => {
    setCurrentIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  return (
    <div className="relative">
      <div className="relative overflow-hidden border border-[#6B8CAE]/30 bg-white/5 h-64">
        <motion.img
          key={currentIndex}
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -50 }}
          transition={{ duration: 0.3 }}
          src={images[currentIndex]}
          alt={`Image ${currentIndex + 1}`}
          className="w-full h-full object-contain cursor-zoom-in"
          onClick={() => onImageClick(images[currentIndex])}
        />
        
        <div 
          className="absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity bg-black/20 cursor-zoom-in"
          onClick={() => onImageClick(images[currentIndex])}
        >
          <div className="w-10 h-10 bg-[#6B8CAE] flex items-center justify-center">
            <ZoomIn size={20} className="text-white" />
          </div>
        </div>

        {images.length > 1 && (
          <>
            <button
              onClick={(e) => { e.stopPropagation(); prevImage(); }}
              className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-[#0F172A]/80 border border-[#6B8CAE]/50 flex items-center justify-center hover:bg-[#6B8CAE]/20 transition-colors"
            >
              <ChevronLeftIcon size={16} className="text-white" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); nextImage(); }}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-[#0F172A]/80 border border-[#6B8CAE]/50 flex items-center justify-center hover:bg-[#6B8CAE]/20 transition-colors"
            >
              <ChevronRight size={16} className="text-white" />
            </button>
          </>
        )}

        <div className="absolute bottom-2 right-2 bg-[#0F172A]/80 px-2 py-1 text-white text-xs font-mono">
          {currentIndex + 1} / {images.length}
        </div>
      </div>

      {images.length > 1 && (
        <div className="flex gap-2 mt-2 overflow-x-auto pb-2">
          {images.map((img, index) => (
            <button
              key={index}
              onClick={() => setCurrentIndex(index)}
              className={`flex-shrink-0 w-16 h-12 border-2 overflow-hidden transition-all ${
                index === currentIndex 
                  ? 'border-[#6B8CAE] opacity-100' 
                  : 'border-transparent opacity-50 hover:opacity-75'
              }`}
            >
              <img
                src={img}
                alt={`Thumbnail ${index + 1}`}
                className="w-full h-full object-cover"
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const TrainingPage = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [lightboxImage, setLightboxImage] = useState(null);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const texts = {
    en: {
      back: 'Back to Services',
      title: 'Training',
      description: 'Practical technical training helps engineering teams work more efficiently, reduce mistakes and use specialized tools with greater confidence. Our training services are focused on real industrial applications, so the acquired knowledge can be applied directly in everyday engineering work.',
      clickToZoom: 'Click image to enlarge',
      catiaTitle: 'CATIA V5 Training',
      catiaItems: [
        'CATIA V5 Basic (Sketch, Part, Assembly and Drafting)',
        'CATIA V5 Advanced (Sketch, Part, Assembly and Drafting)',
        'CATIA V5 Surface Modeling Basic (Generative Shape Design)',
        'CATIA V5 Surface Modeling Advanced (Generative Shape Design)',
        'Custom macro development for CATIA V5'
      ],
      toleranceTitle: 'Tolerance Calculation',
      toleranceItems: [
        'ESOT Tolerance Calculation Tool',
        'Geometric Dimensioning and Tolerancing (GD&T)',
        'Statistical tolerance analysis',
        'Worst-case tolerance stackup analysis'
      ]
    },
    bg: {
      back: 'Обратно към Услуги',
      title: 'Обучение',
      description: 'Практическото техническо обучение помага на инженерните екипи да работят по-ефективно, да намалят грешките и да използват специализирани инструменти с по-голяма увереност. Нашите услуги за обучение са фокусирани върху реални индустриални приложения, така че придобитите знания могат да бъдат приложени директно в ежедневната инженерна работа.',
      clickToZoom: 'Кликнете върху изображението за увеличаване',
      catiaTitle: 'Обучение CATIA V5',
      catiaItems: [
        'CATIA V5 Основно (Скица, Част, Монтаж и Чертеж)',
        'CATIA V5 Разширено (Скица, Част, Монтаж и Чертеж)',
        'CATIA V5 Повърхностно моделиране Основно (Генеративен дизайн на форми)',
        'CATIA V5 Повърхностно моделиране Разширено (Генеративен дизайн на форми)',
        'Персонализирано разработване на макроси за CATIA V5'
      ],
      toleranceTitle: 'Изчисляване на толеранси',
      toleranceItems: [
        'Инструмент за изчисляване на толеранси ESOT',
        'Геометрично оразмеряване и толеранси (GD&T)',
        'Статистически анализ на толеранси',
        'Анализ на толерансно натрупване в най-лош случай'
      ]
    }
  };

  const t = texts[language] || texts.en;

  // CATIA training images - CAD software screens
  const catiaImages = [
    'https://images.unsplash.com/photo-1769149068959-b11392164add?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwxfHxDQUQlMjBzb2Z0d2FyZSUyMHNjcmVlbiUyMGVuZ2luZWVyaW5nJTIwZGVzaWduJTIwaW50ZXJmYWNlfGVufDB8fHx8MTc3NTkwNzQwNHww&ixlib=rb-4.1.0&q=85',
    'https://images.unsplash.com/photo-1600869009498-8d429f88d4f5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwyfHxDQUQlMjBzb2Z0d2FyZSUyMHNjcmVlbiUyMGVuZ2luZWVyaW5nJTIwZGVzaWduJTIwaW50ZXJmYWNlfGVufDB8fHx8MTc3NTkwNzQwNHww&ixlib=rb-4.1.0&q=85',
    'https://images.unsplash.com/photo-1586868538513-51335a0c5337?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwzfHxDQUQlMjBzb2Z0d2FyZSUyMHNjcmVlbiUyMGVuZ2luZWVyaW5nJTIwZGVzaWduJTIwaW50ZXJmYWNlfGVufDB8fHx8MTc3NTkwNzQwNHww&ixlib=rb-4.1.0&q=85',
    'https://images.unsplash.com/photo-1748261759887-faa2a9d76471?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHw0fHxDQUQlMjBzb2Z0d2FyZSUyMHNjcmVlbiUyMGVuZ2luZWVyaW5nJTIwZGVzaWduJTIwaW50ZXJmYWNlfGVufDB8fHx8MTc3NTkwNzQwNHww&ixlib=rb-4.1.0&q=85'
  ];

  // Tolerance calculation images - ESOT Tolerance Calculation Tool
  const toleranceImages = [
    'https://customer-assets.emergentagent.com/job_esot-engineering/artifacts/9b1m3rws_17.JPG'
  ];

  return (
    <div className="min-h-screen bg-[#0F172A]">
      <Navbar />
      
      {lightboxImage && (
        <Lightbox image={lightboxImage} onClose={() => setLightboxImage(null)} />
      )}
      
      <main className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back Button */}
          <motion.button
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            onClick={() => navigate('/')}
            className="flex items-center space-x-2 text-[#6B8CAE] hover:text-white transition-colors mb-8"
            data-testid="back-button"
          >
            <ArrowLeft size={20} />
            <span className="font-mono text-sm uppercase tracking-wider">{t.back}</span>
          </motion.button>

          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-12"
          >
            <div className="flex items-center space-x-4 mb-6">
              <div className="w-16 h-16 bg-[#6B8CAE]/10 border border-[#6B8CAE]/30 flex items-center justify-center">
                <GraduationCap size={32} className="text-[#6B8CAE]" />
              </div>
              <h1 className="text-4xl lg:text-5xl font-bold text-white" data-testid="training-page-title">
                {t.title}
              </h1>
            </div>
            <p className="text-[#94A3B8] text-lg leading-relaxed max-w-4xl">
              {t.description}
            </p>
          </motion.div>

          {/* CATIA V5 Training Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mb-16"
          >
            <div className="grid lg:grid-cols-2 gap-8">
              {/* CATIA Items List */}
              <div className="card-technical p-8">
                <h2 className="text-2xl font-semibold text-white mb-6 flex items-center space-x-3">
                  <BookOpen size={24} className="text-[#6B8CAE]" />
                  <span>{t.catiaTitle}</span>
                </h2>
                <ul className="space-y-3">
                  {t.catiaItems.map((item, index) => (
                    <motion.li
                      key={index}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="flex items-start space-x-3 text-[#94A3B8]"
                    >
                      <ChevronRight size={18} className="text-[#6B8CAE] flex-shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </motion.li>
                  ))}
                </ul>
              </div>

              {/* CATIA Images */}
              <div>
                <ImageCarousel 
                  images={catiaImages} 
                  onImageClick={(img) => setLightboxImage(img)}
                />
              </div>
            </div>
          </motion.div>

          {/* Tolerance Calculation Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <div className="grid lg:grid-cols-2 gap-8">
              {/* Tolerance Items List */}
              <div className="card-technical p-8">
                <h2 className="text-2xl font-semibold text-white mb-6 flex items-center space-x-3">
                  <Calculator size={24} className="text-[#6B8CAE]" />
                  <span>{t.toleranceTitle}</span>
                </h2>
                <ul className="space-y-3">
                  {t.toleranceItems.map((item, index) => (
                    <motion.li
                      key={index}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="flex items-start space-x-3 text-[#94A3B8]"
                    >
                      <ChevronRight size={18} className="text-[#6B8CAE] flex-shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </motion.li>
                  ))}
                </ul>
              </div>

              {/* Tolerance Images */}
              <div>
                <ImageCarousel 
                  images={toleranceImages} 
                  onImageClick={(img) => setLightboxImage(img)}
                />
                <p className="text-[#6B8CAE] text-sm mt-3 text-center font-mono">
                  <ZoomIn size={14} className="inline mr-1" />
                  {t.clickToZoom}
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default TrainingPage;
